import { motion } from "framer-motion";
import { ArrowRight, MonitorCheck, Sparkles, Terminal } from "lucide-react";
import Container from "@/components/ui/Container";
import Button from "@/components/ui/Button";
import Aurora from "@/components/common/Aurora";
import RunPreview from "./RunPreview";

const STATS = [
  { value: "Plain English", label: "no scripts, no selectors" },
  { value: "Per-run cost", label: "tokens + $ tracked" },
  { value: "Auto bug reports", label: "severity + repro steps" },
];

export default function Hero() {
  return (
    <section className="relative overflow-hidden pt-28 pb-16 sm:pt-36">
      <Aurora />
      <Container className="relative">
        <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
          {/* copy */}
          <div className="text-center lg:text-left">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-3.5 py-1.5 text-xs font-semibold text-brand-700 dark:border-brand-500/20 dark:bg-brand-500/10 dark:text-brand-300"
            >
              <Sparkles size={14} />
              AI QA automation, in plain English
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-slate-900 dark:text-white sm:text-5xl lg:text-6xl"
            >
              Test your app by just
              <br className="hidden sm:block" />{" "}
              <span className="gradient-text">describing it.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.12 }}
              className="mx-auto mt-5 max-w-xl text-lg text-slate-600 dark:text-slate-300 lg:mx-0"
            >
              Type a test like you&apos;d explain it to a teammate. Our AI plans the
              steps, runs them in a real browser, catches the bug, and writes the
              report — with the cost of every run in plain sight.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.18 }}
              className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:flex-wrap sm:justify-center lg:justify-start"
            >
              <Button size="lg" to="/signup">
                Start testing free
                <ArrowRight size={18} />
              </Button>
              {/* The two demos stay a pair: with three lg buttons the row wraps
                  on most laptops, and a lone demo button below the fold pair
                  reads as an afterthought rather than the sibling it is. */}
              <div className="flex flex-col gap-3 xs:flex-row">
                <Button size="lg" variant="outline" to="/extension">
                  <Terminal size={18} />
                  Try the extension
                </Button>
                <Button size="lg" variant="outline" to="/desktop-agent">
                  <MonitorCheck size={18} />
                  See the desktop agent
                </Button>
              </div>
            </motion.div>

            {/* mini stats */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-10 grid grid-cols-1 gap-4 border-t border-slate-200/70 pt-6 dark:border-white/10 xs:grid-cols-3"
            >
              {STATS.map((s) => (
                <div key={s.value} className="text-center lg:text-left">
                  <div className="text-sm font-bold text-slate-900 dark:text-white">
                    {s.value}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    {s.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* product shot */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-tr from-brand-500/20 to-fuchsia-500/10 blur-2xl" />
            <div className="relative animate-float">
              <RunPreview />
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
